/**
 * Agent Performance report data + filter options.
 *
 * The report deliberately does NOT fetch on mount. It runs only when `run()`
 * is called with a validated filter set, because the query spans four fact
 * tables and an unbounded default window would be an expensive query nobody
 * asked for. `hasRun` is what the page uses to decide between the empty
 * "choose your filters" state and the results table.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface AgentPerformanceRow {
  agentUserId: string
  agentName: string
  agentId: string | null
  periodStart: string | null
  assigned: number
  closed: number
  workInProgress: number
  reOpened: number
  transferToAgent: number
  transferByAgent: number
  notes: number
  responses: number
  avgResponseSeconds: number | null
  avgResolutionSeconds: number | null
  withinResponseSla: number
  withinResolutionSla: number
  responseSlaPct: number | null
  resolutionSlaPct: number | null
}

export interface ReportFilters {
  timezone: string
  interval: string
  startDate: string
  endDate: string
  campaignIds: string[]
  teamIds: string[]
  agentIds: string[]
}

export interface FilterOption { label: string; value: string }
export interface TimezoneOption extends FilterOption { offset: string }
export interface AgentOption extends FilterOption { code: string | null }

interface FilterOptions {
  timezones: TimezoneOption[]
  campaigns: FilterOption[]
  teams: FilterOption[]
  agents: AgentOption[]
}

/** Options for the filter panel; re-fetched as campaign/team narrow the list. */
export function useAgentPerformanceFilterOptions(
  campaignIds: string[],
  teamIds: string[],
): { options: FilterOptions; loading: boolean } {
  const [options, setOptions] = useState<FilterOptions>({
    timezones: [], campaigns: [], teams: [], agents: [],
  })
  const [loading, setLoading] = useState(true)

  // A fresh array literal every render would re-fire the effect forever, so
  // the dependency is the joined key rather than the arrays themselves.
  const campaignKey = campaignIds.join(',')
  const teamKey = teamIds.join(',')

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams()
        if (campaignIds.length > 0) params.set('campaignIds', campaignIds.join(','))
        if (teamIds.length > 0) params.set('teamIds', teamIds.join(','))
        const res = await api.get<FilterOptions>(
          `/reports/conversations/agent-performance/filters?${params.toString()}`,
        )
        if (!cancelled) setOptions(res)
      } catch {
        if (!cancelled) setOptions({ timezones: [], campaigns: [], teams: [], agents: [] })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [campaignKey, teamKey])

  return { options, loading }
}

interface UseReportResult {
  rows: AgentPerformanceRow[]
  loading: boolean
  error: string | null
  hasRun: boolean
  run: (filters: ReportFilters) => Promise<void>
  reset: () => void
}

export function useAgentPerformanceReport(): UseReportResult {
  const [rows, setRows] = useState<AgentPerformanceRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRun, setHasRun] = useState(false)

  const run = useCallback(async (filters: ReportFilters): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        timezone: filters.timezone,
        interval: filters.interval,
        startDate: filters.startDate,
        endDate: filters.endDate,
        campaignIds: filters.campaignIds.join(','),
      })
      if (filters.teamIds.length > 0) params.set('teamIds', filters.teamIds.join(','))
      if (filters.agentIds.length > 0) params.set('agentIds', filters.agentIds.join(','))

      const res = await api.get<{ rows: AgentPerformanceRow[] }>(
        `/reports/conversations/agent-performance?${params.toString()}`,
      )
      setRows(res.rows ?? [])
      setHasRun(true)
    } catch (err) {
      setRows([])
      setHasRun(true)
      setError(err instanceof Error ? err.message : 'Could not load the report.')
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback((): void => {
    setRows([])
    setHasRun(false)
    setError(null)
  }, [])

  return { rows, loading, error, hasRun, run, reset }
}
